import type { SpreadsheetSheet } from "./xlsx";
import { workbookXlsxBytes } from "./xlsx";

export type StructuredSearchExport = {
  readonly query: string;
  readonly generatedAt: number;
  readonly rows: readonly {
    readonly sourceType: string;
    readonly documentId: string;
    readonly documentTitle: string;
    readonly documentReference: string;
    readonly jurisdiction: string;
    readonly documentKind: string;
    readonly sectionId: string;
    readonly sectionNumber: string;
    readonly sectionTitle: string;
    readonly hierarchy: string;
    readonly sectionText: string;
    readonly sectionMatched: boolean;
    readonly documentMatched: boolean;
    readonly mappingCount: number;
    readonly approvalStatus: string;
    readonly linkedDocumentTitles: readonly string[];
    readonly linkedSectionHeadings: readonly string[];
    readonly coverageStatuses: readonly string[];
    readonly complianceStatuses: readonly string[];
    readonly verificationStatuses: readonly string[];
    readonly sourcePath: string;
  }[];
  readonly mappings: readonly {
    readonly mappingId: string;
    readonly legislationId: string;
    readonly legislationTitle: string;
    readonly legislationReference: string;
    readonly jurisdiction: string;
    readonly legislationSectionId: string;
    readonly legislationSectionNumber: string;
    readonly legislationSectionTitle: string;
    readonly policyId: string;
    readonly policyTitle: string;
    readonly policyDepartment: string;
    readonly policyOwner: string;
    readonly policySectionIds: readonly string[];
    readonly policySectionHeadings: readonly string[];
    readonly coverageStatus: string;
    readonly complianceStatus: string;
    readonly verificationStatus: string;
    readonly approvalStatus: string;
    readonly confidence: string;
    readonly mappingSource: string;
    readonly policyLocation: string;
    readonly obligationSection: string;
    readonly obligationText: string;
    readonly rationale: string;
    readonly gap: string;
    readonly recommendedAction: string;
  }[];
};

const RESULT_HEADERS = [
  "source_type",
  "document_id",
  "document_title",
  "document_reference",
  "jurisdiction",
  "document_kind",
  "section_id",
  "section_number",
  "section_title",
  "hierarchy",
  "section_text",
  "section_matches_query",
  "document_metadata_matches_query",
  "mapping_count",
  "mapping_approval",
  "linked_document_titles",
  "linked_section_headings",
  "coverage_statuses",
  "compliance_statuses",
  "verification_statuses",
  "source_url",
] as const;

const MAPPING_HEADERS = [
  "mapping_id",
  "legislation_id",
  "legislation_title",
  "legislation_reference",
  "jurisdiction",
  "legislation_section_id",
  "legislation_section_number",
  "legislation_section_title",
  "policy_id",
  "policy_title",
  "policy_department",
  "policy_owner",
  "policy_section_ids",
  "policy_section_headings",
  "coverage_status",
  "compliance_status",
  "verification_status",
  "approval_status",
  "confidence",
  "mapping_source",
  "policy_location",
  "obligation_section",
  "obligation_text",
  "rationale",
  "gap",
  "recommended_action",
  "company_document_id",
  "company_document_title",
  "company_document_department",
  "company_document_owner",
  "company_document_section_ids",
  "company_document_section_headings",
  "company_document_location",
] as const;

export const structuredSearchWorkbookSheets = (
  data: StructuredSearchExport,
  origin: string,
): SpreadsheetSheet[] => [
  {
    name: "Search Results",
    rows: [
      RESULT_HEADERS,
      ...data.rows.map((row) => [
        row.sourceType,
        row.documentId,
        row.documentTitle,
        row.documentReference,
        row.jurisdiction,
        row.documentKind,
        row.sectionId,
        row.sectionNumber,
        row.sectionTitle,
        row.hierarchy,
        row.sectionText,
        row.sectionMatched,
        row.documentMatched,
        row.mappingCount,
        row.approvalStatus,
        joined(row.linkedDocumentTitles),
        joined(row.linkedSectionHeadings),
        joined(row.coverageStatuses),
        joined(row.complianceStatuses),
        joined(row.verificationStatuses),
        absoluteUrl(origin, row.sourcePath),
      ]),
    ],
  },
  {
    name: "Mappings",
    rows: [
      MAPPING_HEADERS,
      ...data.mappings.map((mapping) => [
        mapping.mappingId,
        mapping.legislationId,
        mapping.legislationTitle,
        mapping.legislationReference,
        mapping.jurisdiction,
        mapping.legislationSectionId,
        mapping.legislationSectionNumber,
        mapping.legislationSectionTitle,
        mapping.policyId,
        mapping.policyTitle,
        mapping.policyDepartment,
        mapping.policyOwner,
        joined(mapping.policySectionIds),
        joined(mapping.policySectionHeadings),
        mapping.coverageStatus,
        mapping.complianceStatus,
        mapping.verificationStatus,
        mapping.approvalStatus,
        mapping.confidence,
        mapping.mappingSource,
        mapping.policyLocation,
        mapping.obligationSection,
        mapping.obligationText,
        mapping.rationale,
        mapping.gap,
        mapping.recommendedAction,
        mapping.policyId,
        mapping.policyTitle,
        mapping.policyDepartment,
        mapping.policyOwner,
        joined(mapping.policySectionIds),
        joined(mapping.policySectionHeadings),
        mapping.policyLocation,
      ]),
    ],
  },
  {
    name: "About",
    rows: [
      ["field", "value"],
      ["query", data.query],
      ["generated_at", new Date(data.generatedAt).toISOString()],
      ["schema_version", 2],
      ["leaf_section_rows", data.rows.length],
      ["mapping_relationship_rows", data.mappings.length],
      ["structure", "Search Results contains one row per leaf section. Mappings contains one row per company-document-to-legislation relationship for pivots, joins and lookups."],
      ["mapping_columns", "company_document_* columns are canonical. policy_* columns remain as legacy aliases for existing integrations."],
      ["approval", "Approved means human verified. Not approved includes AI-unverified or rejected mapping records. Mixed means both occur for the leaf section."],
    ],
  },
];

export const structuredSearchWorkbookBytes = (
  data: StructuredSearchExport,
  origin: string,
): Uint8Array => workbookXlsxBytes(structuredSearchWorkbookSheets(data, origin));

export const structuredSearchWorkbookFilename = (query: string): string => {
  const slug = query.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return (slug === "" ? "lawstream-search" : `lawstream-search-${slug}`) + ".xlsx";
};

const joined = (values: readonly string[]): string => values.join(" | ");

const absoluteUrl = (origin: string, path: string): string =>
  origin.replace(/\/$/, "") + (path.startsWith("/") ? path : `/${path}`);
